import { inject, Injector } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { signalToObservable } from '@core/utils/signal.utilities';
import {
  createAsyncErrorHandler,
  createErrorMetadata,
  ErrorSeverityMap,
} from '@kerberos-compliance/compliance-shared-ui';
import { combineLatest, lastValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ContractFacade } from '@/contract/facades/contract.facade';

/**
 * A route guard function that prevents navigation to a route if there is an active contract.
 *
 * This guard checks the current contract state via the `ContractFacade` to determine if the user has an active
 * contract. If an active contract is detected, the guard redirects the user to the dashboard and denies
 * access to the route. If no active contract is found, navigation to the target route is allowed.
 *
 * The guard uses Observable streams to monitor the loading state, selected contract, errors, and query
 * information from the `ContractFacade`. Navigation is allowed only if the loading process is complete and
 * there is no active contract or error in the contract data.
 *
 * Error handling is managed using an asynchronous error handler that redirects the user to the logout
 * route on failure. Any errors encountered during the execution are tracked with metadata for observability.
 *
 * @type {CanActivateFn}
 * @returns {Promise<boolean>} A boolean indicating whether navigation to the route is allowed.
 */
export const denyActiveContractGuard: CanActivateFn = async () => {
  const navigationService = inject(NavigationService);
  const contractFacade = inject(ContractFacade);
  const injector = inject(Injector);
  const asyncErrorHandler = createAsyncErrorHandler();

  const result = await asyncErrorHandler(
    async () => {
      const loading$ = signalToObservable(injector, contractFacade.loading);
      const selectedContract$ = signalToObservable(injector, contractFacade.selectedContract);
      const error$ = signalToObservable(injector, contractFacade.error);
      const query$ = signalToObservable(injector, contractFacade.query);

      const [, selected] = await lastValueFrom(
        combineLatest([loading$, selectedContract$, error$, query$]).pipe(
          filter(([loading, selected_, error, query]) => {
            if (loading) {
              return false;
            }
            if (selected_ || error) {
              return true;
            }
            // Allow navigation only when we are not expecting contract data
            return !query?.organizationId;
          }),
          take(1),
        ),
      );

      const isActive = !!selected?.isActive;
      if (isActive) {
        void navigationService.navigateTo('dashboard');
        return false;
      }
      return true;
    },
    {
      metadata: createErrorMetadata('contractNotActiveOnlyGuard', 'Error fetching contract', ErrorSeverityMap.High),
      onError: () => void navigationService.navigateTo('logout'),
    },
  );

  return result ?? false;
};
